/**
 * React Query hooks for canonical pieces management.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type {
  CanonicalPiece,
  CanonicalPieceDetails,
  PromoteCanonicalPieceInput,
  UpdateCanonicalPieceInput,
} from '../types/canonical-piece'
import {
  getCanonicalPieceDetails,
  linkSuggestionToCanonicalPiece,
  listCanonicalPieces,
  promoteCanonicalPiece,
  unlinkSuggestionFromCanonicalPiece,
  updateCanonicalPiece,
} from '../services/api/canonical-pieces'

/**
 * Query key factory for canonical pieces.
 */
export const canonicalPiecesKeys = {
  all: ['canonical-pieces'] as const,
  lists: () => [...canonicalPiecesKeys.all, 'list'] as const,
  list: () => [...canonicalPiecesKeys.lists()] as const,
  details: () => [...canonicalPiecesKeys.all, 'detail'] as const,
  detail: (id: string) => [...canonicalPiecesKeys.details(), id] as const,
}

/**
 * Hook to fetch all canonical pieces.
 */
export function useCanonicalPieces() {
  return useQuery<CanonicalPiece[], Error>({
    queryKey: canonicalPiecesKeys.list(),
    queryFn: listCanonicalPieces,
  })
}

/**
 * Hook to fetch canonical piece details with linked suggestions.
 */
export function useCanonicalPieceDetails(canonicalPieceId: string) {
  return useQuery<CanonicalPieceDetails, Error>({
    queryKey: canonicalPiecesKeys.detail(canonicalPieceId),
    queryFn: () => getCanonicalPieceDetails(canonicalPieceId),
    enabled: !!canonicalPieceId,
  })
}

/**
 * Hook to link a suggestion to a canonical piece.
 */
export function useLinkSuggestion() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      canonicalPieceId,
      suggestionId,
    }: {
      canonicalPieceId: string
      suggestionId: string
    }) => linkSuggestionToCanonicalPiece(canonicalPieceId, suggestionId),
    onSuccess: (_, variables) => {
      // Invalidate both the list and the specific canonical piece details
      queryClient.invalidateQueries({ queryKey: canonicalPiecesKeys.lists() })
      queryClient.invalidateQueries({
        queryKey: canonicalPiecesKeys.detail(variables.canonicalPieceId),
      })
      // Also invalidate freetext suggestions (as suggestion may move from unresolved to resolved)
      queryClient.invalidateQueries({ queryKey: ['freetext-suggestions'] })
    },
  })
}

/**
 * Hook to unlink a suggestion from a canonical piece.
 */
export function useUnlinkSuggestion() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      canonicalPieceId,
      suggestionId,
    }: {
      canonicalPieceId: string
      suggestionId: string
    }) => unlinkSuggestionFromCanonicalPiece(canonicalPieceId, suggestionId),
    onSuccess: (_, variables) => {
      // Invalidate both the list and the specific canonical piece details
      queryClient.invalidateQueries({ queryKey: canonicalPiecesKeys.lists() })
      queryClient.invalidateQueries({
        queryKey: canonicalPiecesKeys.detail(variables.canonicalPieceId),
      })
      // Also invalidate freetext suggestions (as suggestion may become unresolved again)
      queryClient.invalidateQueries({ queryKey: ['freetext-suggestions'] })
    },
  })
}

/**
 * Hook to update a canonical piece.
 */
export function useUpdateCanonicalPiece() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({
      canonicalPieceId,
      input,
    }: {
      canonicalPieceId: string
      input: UpdateCanonicalPieceInput
    }) => updateCanonicalPiece(canonicalPieceId, input),
    onSuccess: (response) => {
      // Invalidate the canonical pieces list
      queryClient.invalidateQueries({ queryKey: canonicalPiecesKeys.lists() })

      // If ID changed, invalidate both old and new details
      if (response.id_changed && response.old_id) {
        queryClient.invalidateQueries({
          queryKey: canonicalPiecesKeys.detail(response.old_id),
        })
      }

      // Invalidate the current/new canonical piece details
      queryClient.invalidateQueries({
        queryKey: canonicalPiecesKeys.detail(response.canonical_piece.canonical_piece_id),
      })

      // Also invalidate freetext suggestions (as entries may move in/out of freetext status)
      queryClient.invalidateQueries({ queryKey: ['freetext-suggestions'] })
    },
  })
}

/**
 * Hook to promote a canonical piece to a Wikidata reference.
 */
export function usePromoteCanonicalPiece() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ pieceId, input }: { pieceId: string; input: PromoteCanonicalPieceInput }) =>
      promoteCanonicalPiece(pieceId, input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: canonicalPiecesKeys.all })
      queryClient.invalidateQueries({ queryKey: ['freetext-suggestions'] })
      queryClient.invalidateQueries({ queryKey: ['suggestions'] })
    },
  })
}
